import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import duration from "dayjs/plugin/duration";
import { useDispatch } from "react-redux";
import { setBuffer } from "Redux/Screen/Action";
import DateTime from "Utils/DateTime";
import { PatientInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

interface UseMonthEndSchedulerOptions {
  orgTimezone: string;
  onForceExit?: (
    alertText: string,
    // exitType: "buffer" | "countdownEnd"
  ) => void;
  showCountdownOnUI: boolean;
  bufferTimeInSec: number
}

interface StartCounterType {
  target: dayjs.Dayjs,
}

const useMonthEndSchedulerWithSmartCountdown = (
  props: UseMonthEndSchedulerOptions
): {
  isInBuffer: boolean,
  countdown: string
} => {
  const {
    orgTimezone,
    onForceExit,
    showCountdownOnUI,
    bufferTimeInSec
  } = props;

  const dispatch = useDispatch();
  const { patientOrg }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient
  );
  const [isInBuffer, setIsInBuffer] = useState(false);
  const [countdown, setCountdown] = useState<string>("");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    dispatch(setBuffer(countdown));
  }, [countdown])

  useEffect(() => {
    // 🟢 Start logic immediately on mount

    // run the main scheduler immediately on mount / dependency change
    CalculateSchedule();

    // cleanup when component unmounts or dependencies change
    return () => {

      // clear scheduled timeout
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      // clear per-second interval
      if (intervalRef.current) clearInterval(intervalRef.current);
      dispatch(setBuffer(""));
    };
  }, []);

  // main function that calculates windows and schedules next check
  const CalculateSchedule = (): void => {
    // current time in organization's timezone
    const now = DateTime.CurrentDateTimeInPatOrgLocTZ(
      patientOrg.practiceLocationTimezone
    );

    const endOfMonth = now.endOf("month");
    const countdownStart = endOfMonth.subtract(2, "hour");
    const countdownEnd = endOfMonth
    const inCountdown = now.isAfter(countdownStart) &&
      now.isBefore(countdownEnd);

    let startOfMonth = now.startOf("month");
    if (inCountdown) startOfMonth = now.add(1, "month").startOf("month");
    const bufferStart = startOfMonth.subtract(1, "second");
    const bufferEnd = bufferStart.add(bufferTimeInSec, "second");
    const inBuffer = now.isSame(bufferStart) ||
      (now.isAfter(bufferStart) && now.isBefore(bufferEnd));
    setIsInBuffer(inBuffer);

    // 🚫 Force exit if user is inside buffer and still doing activity
    if (inBuffer && onForceExit) {
      const diffMs = bufferEnd.diff(now); // milliseconds difference to target
      onForceExit(
        "The billable time for the month is getting processed," +
        " you can resume the services in next " + `
        ${DateTime.CalculateTimeDiff(diffMs)}`,
        // isCountdownEnd ? "countdownEnd" : "buffer"
      );
    }

    if (inCountdown && showCountdownOnUI) {
      StartCountdown({ target: countdownEnd });
    } else if (inBuffer && showCountdownOnUI) {
      StartCountdown({ target: bufferEnd });
    } else {
      setCountdown("");
      //   // clear any existing per-second interval
      if (intervalRef.current) clearInterval(intervalRef.current);
    }

    // Schedule next boundary check (minimize background timers)
    let nextCheck: dayjs.Dayjs;

    // next important moment: countdownStart
    if (now.isBefore(countdownStart)) nextCheck = countdownStart;

    // then bufferStart
    else if (now.isBefore(bufferStart)) nextCheck = bufferStart;

    // then bufferEnd
    else if (now.isBefore(bufferEnd)) nextCheck = bufferEnd;

    // otherwise schedule for next month's countdownStart (not really required)
    else nextCheck = endOfMonth;

    // compute ms until nextCheck; min 10s for safety
    const delay = Math.max(nextCheck.diff(now, "millisecond"), 1000 * 10);

    // schedule a one-shot timeout to re-run calculateSchedule
    timeoutRef.current = setTimeout(CalculateSchedule, delay);
  };

  const StartCountdown = (props: StartCounterType): void => {
    const { target } = props;

    // clear previous interval if any
    if (intervalRef.current) clearInterval(intervalRef.current);

    // start a per-second interval to update countdown text
    intervalRef.current = setInterval(() => {
      const current = dayjs().tz(orgTimezone); // current time in orgTZ
      const diffMs = target.diff(current);
      if (diffMs <= 0) { // if target already reached or passed
        setCountdown("");
        // stop updating once target is reached
        intervalRef.current && clearInterval(intervalRef.current);
        return;
      }
      // set human readable countdown string in state
      setCountdown(DateTime.CalculateTimeDiff(diffMs));
      // run every second
    }, 1000);
  };

  return { isInBuffer, countdown };
}
export default useMonthEndSchedulerWithSmartCountdown;
